// Arabic Lab → Today and the week in review: pure helpers shared by the app and tests (no imports). Today gathers what is
// due (or weak) across every course and Lab section into one session; the week in review summarises the last seven days.
type Rec={item_key:string;skill:string;course_notebook_id:string;label:string;seen:number;again:number;last_rating:string;interval_days:number;next_review_at:string|null;last_at:string|null}
export type TodayMode='due'|'weak'
const DAY=86400000
const isDue=(r:Rec,now:number)=>!r.next_review_at||Date.parse(r.next_review_at)<=now
// The same rule as the dashboard (arabicProgressCore masteryOf): missed last time, or in two of every five answers.
export const isWeak=(r:Rec)=>r.last_rating==='again'||(r.seen>=2&&r.again/r.seen>=.4)
const missRate=(r:Rec)=>r.seen?r.again/r.seen:0

/**
 * The records for one session: due ones, most overdue first, or weak ones, most often missed first. Only records the
 * app can show here (`canShow`) are taken; an item practised in several skills appears once (its most pressing skill).
 */
export function sessionRecords<R extends Rec>(records:R[],mode:TodayMode,canShow:(r:R)=>boolean,now=Date.now(),limit=20):R[]{
 const pool=records.filter(r=>canShow(r)&&(mode==='due'?isDue(r,now):isWeak(r)))
 pool.sort(mode==='due'
  ?(a,b)=>Date.parse(a.next_review_at||'0')-Date.parse(b.next_review_at||'0')||a.item_key.localeCompare(b.item_key)
  :(a,b)=>missRate(b)-missRate(a)||Date.parse(b.last_at||'0')-Date.parse(a.last_at||'0')||a.item_key.localeCompare(b.item_key))
 const seen=new Set<string>(),out:R[]=[]
 for(const r of pool){if(seen.has(r.item_key))continue;seen.add(r.item_key);out.push(r);if(out.length>=limit)break}
 return out
}
/** Records the app cannot show in a session, counted by where they can be practised instead. */
export function elsewhere<R extends Rec>(records:R[],mode:TodayMode,canShow:(r:R)=>boolean,now=Date.now()){
 const counts:Record<string,number>={},seen=new Set<string>()
 for(const r of records){
  if(canShow(r)||seen.has(r.item_key)||!(mode==='due'?isDue(r,now):isWeak(r)))continue
  seen.add(r.item_key);const where=r.item_key.split(':')[0];counts[where]=(counts[where]||0)+1
 }
 return counts
}
/** A missed card comes back once at the end of the session, so each one is met again before finishing. */
export function requeue<T extends {item_key:string}>(queue:T[],at:number,missed:boolean,returned:Set<string>):T[]{
 const card=queue[at]
 if(!missed||!card||returned.has(card.item_key))return queue
 returned.add(card.item_key)
 return[...queue,card]
}

type Day={day:string;answers:number;right:number}
type Checkpoint={course_notebook_id:string;section:string;best:number;taken_at:string|null}
export type WeekSummary={answers:number;right:number;days:number;mastered:{item_key:string;label:string}[];slipped:{item_key:string;label:string}[];close:Checkpoint[]}
/**
 * The last seven days: answers and days practised (from the calendar, local days), items answered this week that are
 * now mastered, items whose last answer this week was missed (slipped to weak), and checkpoints scored 60–79%.
 */
export function weekSummary(records:Rec[],calendar:Day[],checkpoints:Checkpoint[],now=Date.now(),passMark=.8):WeekSummary{
 const week=calendar.slice(-7),since=now-7*DAY
 const thisWeek=(r:Rec)=>!!r.last_at&&Date.parse(r.last_at)>=since
 const once=(list:Rec[])=>{const seen=new Set<string>();return list.filter(r=>!seen.has(r.item_key)&&!!seen.add(r.item_key)).map(r=>({item_key:r.item_key,label:r.label}))}
 const weakItems=new Set(records.filter(isWeak).map(r=>r.item_key))
 return{
  answers:week.reduce((n,d)=>n+d.answers,0),right:week.reduce((n,d)=>n+d.right,0),days:week.filter(d=>d.answers>0).length,
  mastered:once(records.filter(r=>thisWeek(r)&&r.interval_days>=21&&!weakItems.has(r.item_key))),
  slipped:once(records.filter(r=>thisWeek(r)&&r.last_rating==='again')),
  close:checkpoints.filter(c=>c.best>=.6&&c.best<passMark).sort((a,b)=>b.best-a.best),
 }
}
